import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Card,
    TextField,
    Typography,
    MenuItem,
    Button,
    Alert,
    CircularProgress,
    InputAdornment,
    IconButton,
    Link,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import { 
    Visibility, 
    VisibilityOff, 
    Email, 
    Lock, 
    Person,
    Phone,
    Business,
    Description as DescriptionIcon,
    ExpandMore as ExpandMoreIcon,
    EmailOutlined as EmailOutlinedIcon,
    PhoneOutlined as PhoneOutlinedIcon
} from "@mui/icons-material";
import { setVendorCredentials } from "../../features/admin/authSlice.ts";
import vendorPortalIllustration from "../../assets/vendor_portal_illustration.jpg";

const VendorLogin = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    const [currentView, setCurrentView] = useState("login");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [fieldErrors, setFieldErrors] = useState<Record<string,string>>({});
    const [successMsg, setSuccessMsg] = useState("");
    const [loading, setLoading] = useState(false);
    const [accountStatusDialog, setAccountStatusDialog] = useState<{open:boolean; status:string; reason:string}>({open:false,status:"",reason:""});
    // Admin support contact shown on the block/reject popup below, fetched
    // once on mount from a safe public endpoint (never the full smtp-list,
    // which also returns SMTP/SMS credentials — see publicSupportContact
    // in routes/globalsettings.js for why).
    const [supportContact, setSupportContact] = useState<{email: string | null; contact: string | null}>({email: null, contact: null});

    const [loginEmail, setLoginEmail] = useState("");
    const [loginPassword, setLoginPassword] = useState("");

    const [regFirstName, setRegFirstName] = useState("");
    const [regLastName, setRegLastName] = useState("");
    const [regEmail, setRegEmail] = useState("");
    const [regCountryCode, setRegCountryCode] = useState("+971");
    const [regContact, setRegContact] = useState("");
    const [regDescription, setRegDescription] = useState("");
    const [regPassword, setRegPassword] = useState("");
    const [regConfirmPassword, setRegConfirmPassword] = useState("");

    // ── Company / vendor-verification details ──────────────────────────
    // Collected up front so admin has what they need to review the
    // application in Vendor Applications before approving product access.
    const [regBusinessName, setRegBusinessName] = useState("");
    const [regBusinessType, setRegBusinessType] = useState("shop");
    const [regTrnNumber, setRegTrnNumber] = useState("");
    const [regCompanyAddress, setRegCompanyAddress] = useState("");
    const [regCompanyLocation, setRegCompanyLocation] = useState("");
    // Pickup address collected during signup. Coordinates can be fetched automatically.
    const [regPickupName, setRegPickupName] = useState("");
    const [regPickupType, setRegPickupType] = useState("warehouse");
    const [regStreet, setRegStreet] = useState("");
    const [regAddressCountry, setRegAddressCountry] = useState("");
    const [regFullPickupAddress, setRegFullPickupAddress] = useState("");
    const [regCity, setRegCity] = useState("");
    const [regState, setRegState] = useState("");
    const [regPostalCode, setRegPostalCode] = useState("");
    const [regLatitude, setRegLatitude] = useState("");
    const [regLongitude, setRegLongitude] = useState("");
    const [fetchingLocation, setFetchingLocation] = useState(false);
    const [regWebsite, setRegWebsite] = useState("");
    const [regCertificateFile, setRegCertificateFile] = useState(null);

    const [otpEmail, setOtpEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [showOtpInput, setShowOtpInput] = useState(false);

    const [resetEmail, setResetEmail] = useState("");
    const [resetOtp, setResetOtp] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmNewPassword, setConfirmNewPassword] = useState("");
    const [showResetOtpInput, setShowResetOtpInput] = useState(false);
    const [showPasswordFields, setShowPasswordFields] = useState(false);

    // Post-login pickup-address check. If the vendor already has an
    // address on file, we ask them to confirm it (or add a new one)
    // instead of silently reusing it — a vendor may have moved, or may
    // want a second pickup point. If they have none at all, we skip
    // straight to the "add address" dialog on their profile page.
    const [addressPromptOpen, setAddressPromptOpen] = useState(false);
    const [existingAddress, setExistingAddress] = useState<any>(null);
    const [addressCheckLoading, setAddressCheckLoading] = useState(false);
    // Pickup address is optional at signup — collapsed by default. The
    // vendor can click to expand, view/edit it, and submit; or leave it
    // closed and submit without it (they'll be prompted to add one later,
    // before their first product upload).
    const [addressExpanded, setAddressExpanded] = useState(false);

    const BASE_URL = (process.env.REACT_APP_SERVER_ENDPOINT || '').replace(/\/$/, '');
    // Backend mounts vendor authentication under /user.
    // Keep this safe if the environment already contains /user.
    const USER_API_URL = BASE_URL.endsWith('/user') ? BASE_URL : `${BASE_URL}/user`;
    const AUTH_KEY = "7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3";

    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${BASE_URL}/globalsettings/publicSupportContact`, {
                    headers: { authkey: AUTH_KEY },
                });
                const data = await res.json().catch(() => ({}));
                if (data && data.error === false) {
                    setSupportContact({ email: data.support_email || null, contact: data.contact || null });
                }
            } catch (err) {
                console.warn('Failed to load support contact:', err);
            }
        })();
    }, []);


    // Automatically detect the browser's current location when the vendor opens
    // the signup form, then reverse-geocode it into one complete address. The
    // vendor can still edit the resulting full address before submitting.
    const fetchCurrentLocation = async () => {
        if (!navigator.geolocation) {
            setErrorMsg("Location detection is not supported by this browser. Please enter the full pickup address.");
            return;
        }
        setFetchingLocation(true);
        setErrorMsg("");
        try {
            const position = await new Promise<GeolocationPosition>((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                    enableHighAccuracy: true,
                    timeout: 12000,
                    maximumAge: 300000,
                });
            });
            const { latitude, longitude } = position.coords;
            setRegLatitude(String(latitude));
            setRegLongitude(String(longitude));

            const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(latitude)}&lon=${encodeURIComponent(longitude)}&addressdetails=1`,
                { headers: { Accept: 'application/json', 'Accept-Language': 'en' } }
            );
            if (!response.ok) throw new Error('Reverse geocoding failed');
            const data = await response.json();
            const a = data?.address || {};
            const city = a.city || a.town || a.village || a.municipality || a.county || '';
            const state = a.state || a.region || '';
            const postal = a.postcode || '';
            const street = [a.house_number, a.road || a.pedestrian || a.residential].filter(Boolean).join(' ');
            const full = data?.display_name || [street, a.suburb || a.neighbourhood, city, state, postal, a.country].filter(Boolean).join(', ');

            setRegStreet(street);
            setRegAddressCountry(a.country || '');
            setRegCity(city);
            setRegState(state);
            const detectedIso = String(a.country_code || '').toUpperCase();
            const detectedCountry = COUNTRY_CODES.find(([, , iso]) => iso === detectedIso);
            if (detectedCountry) setRegCountryCode(detectedCountry[0]);
            setRegPostalCode(postal);
            setRegCompanyLocation(city || state || a.country || '');
            setRegCompanyAddress(full);
            setRegFullPickupAddress(full);
            if (!regPickupName.trim()) setRegPickupName(regBusinessName.trim() || 'Main Pickup Location');
        } catch (error: any) {
            const message = error?.code === 1
                ? 'Location permission was denied. Please allow location access or enter the full pickup address manually.'
                : 'Unable to fetch the current address. Please enter the full pickup address manually.';
            setErrorMsg(message);
        } finally {
            setFetchingLocation(false);
        }
    };

    useEffect(() => {
        if (currentView === 'register' && !showOtpInput && !regFullPickupAddress.trim()) {
            fetchCurrentLocation();
        }
        // Intentionally run when entering signup only.
        // The dependency list controls when the location lookup is triggered.
    }, [currentView, showOtpInput]);

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Do not enforce a fixed mobile-number length in the vendor registration form.
    // Country/number formats vary, so the user can enter any number of digits.
    // We only require that at least one digit is entered.
    const validatePhone = (phone, countryCode) => {
        const digits = String(phone || "").replace(/\D/g, "");
        return digits.length > 0;
    };

    const COUNTRY_CODES = useMemo(() => [
      ["+971","United Arab Emirates","AE"],["+91","India","IN"],["+1","United States / Canada","US"],["+44","United Kingdom","GB"],["+93","Afghanistan","AF"],["+355","Albania","AL"],["+213","Algeria","DZ"],["+376","Andorra","AD"],["+244","Angola","AO"],["+54","Argentina","AR"],["+374","Armenia","AM"],["+61","Australia","AU"],["+43","Austria","AT"],["+994","Azerbaijan","AZ"],["+973","Bahrain","BH"],["+880","Bangladesh","BD"],["+375","Belarus","BY"],["+32","Belgium","BE"],["+501","Belize","BZ"],["+229","Benin","BJ"],["+975","Bhutan","BT"],["+591","Bolivia","BO"],["+387","Bosnia and Herzegovina","BA"],["+267","Botswana","BW"],["+55","Brazil","BR"],["+673","Brunei","BN"],["+359","Bulgaria","BG"],["+226","Burkina Faso","BF"],["+257","Burundi","BI"],["+855","Cambodia","KH"],["+237","Cameroon","CM"],["+238","Cape Verde","CV"],["+236","Central African Republic","CF"],["+235","Chad","TD"],["+56","Chile","CL"],["+86","China","CN"],["+57","Colombia","CO"],["+269","Comoros","KM"],["+242","Congo","CG"],["+243","DR Congo","CD"],["+506","Costa Rica","CR"],["+385","Croatia","HR"],["+53","Cuba","CU"],["+357","Cyprus","CY"],["+420","Czechia","CZ"],["+45","Denmark","DK"],["+253","Djibouti","DJ"],["+593","Ecuador","EC"],["+20","Egypt","EG"],["+503","El Salvador","SV"],["+240","Equatorial Guinea","GQ"],["+291","Eritrea","ER"],["+372","Estonia","EE"],["+268","Eswatini","SZ"],["+251","Ethiopia","ET"],["+679","Fiji","FJ"],["+358","Finland","FI"],["+33","France","FR"],["+241","Gabon","GA"],["+220","Gambia","GM"],["+995","Georgia","GE"],["+49","Germany","DE"],["+233","Ghana","GH"],["+30","Greece","GR"],["+502","Guatemala","GT"],["+224","Guinea","GN"],["+245","Guinea-Bissau","GW"],["+592","Guyana","GY"],["+509","Haiti","HT"],["+504","Honduras","HN"],["+36","Hungary","HU"],["+354","Iceland","IS"],["+62","Indonesia","ID"],["+98","Iran","IR"],["+964","Iraq","IQ"],["+353","Ireland","IE"],["+972","Israel","IL"],["+39","Italy","IT"],["+225","Ivory Coast","CI"],["+81","Japan","JP"],["+962","Jordan","JO"],["+7","Kazakhstan / Russia","KZ"],["+254","Kenya","KE"],["+686","Kiribati","KI"],["+965","Kuwait","KW"],["+996","Kyrgyzstan","KG"],["+856","Laos","LA"],["+371","Latvia","LV"],["+961","Lebanon","LB"],["+266","Lesotho","LS"],["+231","Liberia","LR"],["+218","Libya","LY"],["+423","Liechtenstein","LI"],["+370","Lithuania","LT"],["+352","Luxembourg","LU"],["+261","Madagascar","MG"],["+265","Malawi","MW"],["+60","Malaysia","MY"],["+960","Maldives","MV"],["+223","Mali","ML"],["+356","Malta","MT"],["+692","Marshall Islands","MH"],["+222","Mauritania","MR"],["+230","Mauritius","MU"],["+52","Mexico","MX"],["+691","Micronesia","FM"],["+373","Moldova","MD"],["+377","Monaco","MC"],["+976","Mongolia","MN"],["+382","Montenegro","ME"],["+212","Morocco","MA"],["+258","Mozambique","MZ"],["+95","Myanmar","MM"],["+264","Namibia","NA"],["+674","Nauru","NR"],["+977","Nepal","NP"],["+31","Netherlands","NL"],["+64","New Zealand","NZ"],["+505","Nicaragua","NI"],["+227","Niger","NE"],["+234","Nigeria","NG"],["+850","North Korea","KP"],["+389","North Macedonia","MK"],["+47","Norway","NO"],["+968","Oman","OM"],["+92","Pakistan","PK"],["+680","Palau","PW"],["+970","Palestine","PS"],["+507","Panama","PA"],["+675","Papua New Guinea","PG"],["+595","Paraguay","PY"],["+51","Peru","PE"],["+63","Philippines","PH"],["+48","Poland","PL"],["+351","Portugal","PT"],["+974","Qatar","QA"],["+40","Romania","RO"],["+250","Rwanda","RW"],["+685","Samoa","WS"],["+378","San Marino","SM"],["+239","Sao Tome and Principe","ST"],["+966","Saudi Arabia","SA"],["+221","Senegal","SN"],["+381","Serbia","RS"],["+248","Seychelles","SC"],["+232","Sierra Leone","SL"],["+65","Singapore","SG"],["+421","Slovakia","SK"],["+386","Slovenia","SI"],["+677","Solomon Islands","SB"],["+252","Somalia","SO"],["+27","South Africa","ZA"],["+82","South Korea","KR"],["+211","South Sudan","SS"],["+34","Spain","ES"],["+94","Sri Lanka","LK"],["+249","Sudan","SD"],["+597","Suriname","SR"],["+46","Sweden","SE"],["+41","Switzerland","CH"],["+963","Syria","SY"],["+886","Taiwan","TW"],["+992","Tajikistan","TJ"],["+255","Tanzania","TZ"],["+66","Thailand","TH"],["+670","Timor-Leste","TL"],["+228","Togo","TG"],["+676","Tonga","TO"],["+216","Tunisia","TN"],["+90","Turkey","TR"],["+993","Turkmenistan","TM"],["+688","Tuvalu","TV"],["+256","Uganda","UG"],["+380","Ukraine","UA"],["+598","Uruguay","UY"],["+998","Uzbekistan","UZ"],["+678","Vanuatu","VU"],["+379","Vatican City","VA"],["+58","Venezuela","VE"],["+84","Vietnam","VN"],["+967","Yemen","YE"],["+260","Zambia","ZM"],["+263","Zimbabwe","ZW"],
      ["+1242","Bahamas","BS"],["+1246","Barbados","BB"],["+1441","Bermuda","BM"],["+1345","Cayman Islands","KY"],["+1767","Dominica","DM"],["+1809","Dominican Republic","DO"],["+1473","Grenada","GD"],["+1876","Jamaica","JM"],["+1868","Trinidad and Tobago","TT"],["+1649","Turks and Caicos Islands","TC"],["+1787","Puerto Rico","PR"],["+1340","US Virgin Islands","VI"],["+1284","British Virgin Islands","VG"],["+599","Curacao / Caribbean Netherlands","CW"],["+297","Aruba","AW"],["+590","Guadeloupe / Saint Martin","GP"],["+596","Martinique","MQ"],["+262","Reunion / Mayotte","RE"],["+290","Saint Helena","SH"],["+290","Ascension Island","AC"],["+508","Saint Pierre and Miquelon","PM"],["+1784","Saint Vincent and the Grenadines","VC"],["+1758","Saint Lucia","LC"],["+1869","Saint Kitts and Nevis","KN"],["+1671","Guam","GU"],["+670","Northern Mariana Islands","MP"]
    ].sort((a,b) => a[1].localeCompare(b[1])), []);


    const validateVendorPassword = (password) => {
        return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/.test(password);
    };
const checkVendorAddressAndProceed = async (token) => {
    setAddressCheckLoading(true);
    try {
        const response = await fetch(`${BASE_URL}/product/warehouselist`, {
            method: "POST",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
                "Accept": "application/json",
                "authkey": AUTH_KEY,
                Authorization: `Bearer ${token}`,
            },
            body: new URLSearchParams(),
        });
        const result = await response.json();
        const list = result?.warehouses || result?.result || [];

        if (!Array.isArray(list) || list.length === 0) {
            // No pickup address at all yet — go straight to adding one.
            navigate("/adminProfile", {
                state: {
                    openAddressDialog: true,
                    addressPrompt: "Welcome! Please add your pickup address (warehouse, shop, or home) before adding products.",
                },
            });
            return;
        }

        // A pickup address entered during signup is already saved and should be
        // reused automatically after login. Do not show an extra confirmation
        // dialog or ask the vendor to add the same address again.
        const defaultAddress = list.find((w) => Number(w.is_default) === 1) || list[0];
        setExistingAddress(defaultAddress);
        navigate("/productManagement");
    } catch (error) {
        // Don't block login over a failed address check — let them in and
        // they can manage addresses from their profile any time.
        navigate("/productManagement");
    } finally {
        setAddressCheckLoading(false);
    }
};

const handleUseExistingAddress = () => {
    setAddressPromptOpen(false);
    navigate("/productManagement");
};

const handleAddNewAddress = () => {
    setAddressPromptOpen(false);
    navigate("/adminProfile", {
        state: {
            openAddressDialog: true,
            addressPrompt: "Add a new pickup address, or edit/remove your existing ones below.",
        },
    });
};

const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    if (!loginEmail.trim()) {
        setErrorMsg("Email is required");
        setLoading(false);
        return;
    }

    if (!validateEmail(loginEmail)) {
        setErrorMsg("Please enter a valid email address");
        setLoading(false);
        return;
    }

    if (!loginPassword.trim()) {
        setErrorMsg("Password is required");
        setLoading(false);
        return;
    }

    try {
        const response = await fetch(`${USER_API_URL}/vendorlogin`, {
            method: "POST",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
                "Accept": "application/json",
                "authkey": AUTH_KEY,
            },
            body: new URLSearchParams({
                user_email: loginEmail.trim(),
                user_pass: loginPassword,
            }),
        });

        const result = await response.json();

        // Blocked/rejected accounts return a dedicated status response so the
        // vendor can see the admin-provided reason instead of a generic login
        // error.
        if (result?.code === "VENDOR_ACCOUNT_RESTRICTED" || result?.status === "blocked" || result?.status === "rejected") {
            setAccountStatusDialog({
                open: true,
                status: result.status || "restricted",
                reason: String(result.reason || result.message || "Please contact the administrator.").trim() || "Please contact the administrator."
            });
            setLoading(false);
            return;
        }

        const payload = result?.data && typeof result.data === "object" ? result.data : result;

        if (!result.error && payload.token) {
            // Debug: Log the entire response
            console.log("=== FULL RESPONSE ===");
            console.log(JSON.stringify(result, null, 2));
            console.log("Result object:", result);
            console.log("Type of result:", typeof result);
            console.log("Is array?:", Array.isArray(result));
            if (result.data) console.log("result.data:", result.data);
            if (result.user) console.log("result.user:", result.user);
            console.log("=====================");
            
            // Extract vendor_id - try multiple possible locations
            let vendorId = payload.vendor_id || payload.vendorId || payload.VendorId || payload.id;
            
            // If not found at root level, check other possible locations
            if (!vendorId) {
                vendorId = payload.vendor_id || payload.VendorId || payload.vendorId || payload.id;
            }
            
            const userName = payload.user_name || payload.userName || payload.name || `Vendor User`;

            // Allow login even if vendorId is missing (will use as fallback)
            if (!vendorId) {
                console.warn("Warning: vendor_id not found in response");
                // Don't fail here - use a temporary ID and let backend handle it
                setErrorMsg("Vendor ID was not returned by the server. Please contact the administrator.");
                setLoading(false);
                return;
            }

            // Clear any existing admin session completely first. If a
            // Super Admin/Admin/Operation session logged in earlier on this
            // browser, its role/permission keys (admin_type, role,
            // admin_permissions, admin_perm_map, admin_role_id, etc.) stay
            // in localStorage unless removed here. Both AdminHeader and
            // AdminSlideMenu read `admin_type` / `role` BEFORE `user_role`,
            // so a leftover "super_admin" would make a vendor session show
            // "Super Admin" in the header and grant it full menu access
            // (Order Management, Return, User List, ...) instead of being
            // restricted to Product Management.
            localStorage.removeItem("admin_token");
            localStorage.removeItem("admin_password");
            localStorage.removeItem("admin_type");
            localStorage.removeItem("role");
            localStorage.removeItem("admin_id");
            localStorage.removeItem("admin_name");
            localStorage.removeItem("admin_email");
            localStorage.removeItem("admin_role_id");
            localStorage.removeItem("admin_permissions");
            localStorage.removeItem("admin_perm_map");
            localStorage.removeItem("user_role");

            // Store vendor credentials in localStorage
            localStorage.setItem("vendor_token", payload.token);
            localStorage.setItem("user_role", "vendor");
            localStorage.setItem("user_email", loginEmail.trim());
            localStorage.setItem("vendor_id", vendorId.toString());
            localStorage.setItem("user_name", userName);

            // Save role-based permissions (from the "Vendor" role in Role
            // Master → Role Mapping, resolved server-side in /vendorlogin).
            // adminSlidemenu.tsx / PermissionContext.tsx / App.tsx's
            // ProtectedRoute all read `admin_permissions` + `admin_role_id`
            // the same way they do for admin logins, so whatever an admin
            // grants the "Vendor" role opens up automatically here.
            if (payload.permissions && Object.keys(payload.permissions).length > 0) {
                localStorage.setItem("admin_permissions", JSON.stringify(payload.permissions));
            } else {
                localStorage.removeItem("admin_permissions");
            }
            if (payload.role_id) {
                localStorage.setItem("admin_role_id", String(payload.role_id));
            } else {
                localStorage.removeItem("admin_role_id");
            }
            localStorage.removeItem("admin_perm_map"); // force SlideMenu to rebuild fresh

            // Dispatch Redux action to store credentials
            dispatch(setVendorCredentials({
                token: payload.token,
                email: loginEmail.trim(),
                vendorId: vendorId,
                userName: userName,
                role: "vendor"
            }));

            // Company-verification gate: a seller who applied but hasn't
            // been approved by an admin yet (see Vendor Applications in the
            // back office) can log in to see their status, but shouldn't be
            // dropped into Product Management — every product API call
            // would just 403. Surface that clearly instead.
            localStorage.setItem("vendor_approval_status", payload.approval_status || "pending");
            if (payload.approval_status && payload.approval_status !== "approved") {
                setLoading(false);
                if (payload.approval_status === "rejected" || payload.approval_status === "blocked") {
                    setAccountStatusDialog({
                        open: true,
                        status: payload.approval_status,
                        reason: String(payload.approval_notes || payload.reason || "Please contact the administrator.").trim() || "Please contact the administrator."
                    });
                    localStorage.removeItem("vendor_token");
                    localStorage.removeItem("vendor_approval_status");
                } else {
                    setSuccessMsg("Login successful. Your seller application is still under review — product uploads will unlock once an admin approves it.");
                }
                return;
            }

            setSuccessMsg("Login successful!");

            // Before landing on Product Management, make sure this vendor
            // has a pickup address on file — either confirm the one they
            // already have, or send them to add one. Without a pickup
            // address, adding a product fails server-side.
            setTimeout(() => {
                checkVendorAddressAndProceed(payload.token);
            }, 800);
        } else {
            setErrorMsg(result.message || "Invalid credentials");
        }
    } catch (error) {
        setErrorMsg("Network error: Unable to connect to server");
    } finally {
        setLoading(false);
    } 
};
    const handleRegistration = async (e) => {
        e.preventDefault();
        if (loading) return; // Prevent double-click / duplicate registration requests.
        setLoading(true);
        setErrorMsg("");
        setSuccessMsg("");
        setFieldErrors({});

        const fail = (field, message) => {
            // Show validation errors both next to the field and in the global
            // top error popup so the user never misses an invalid value.
            setFieldErrors(prev => ({ ...prev, [field]: message }));
            setErrorMsg(message);
            setLoading(false);
        };
        if (!regFirstName.trim()) { fail("firstName", "First name is required"); return; }

        if (!regLastName.trim()) { fail("lastName", "Last name is required"); return; }

        if (!validateEmail(regEmail)) { fail("email", "Please enter a valid email address"); return; }

        if (!validatePhone(regContact, regCountryCode)) { fail("contact", "Please enter a mobile number"); return; }

        if (!validateVendorPassword(regPassword)) { fail("password", "Password must be at least 8 characters with uppercase, lowercase, number and special character"); return; }

        if (regPassword !== regConfirmPassword) { fail("confirmPassword", "Passwords do not match"); return; }

        if (!regBusinessName.trim()) { fail("businessName", "Company / business name is required"); return; }

        // Pickup address is optional at signup — vendors can add/edit it
        // later from their profile before their first product upload.

        if (!regTrnNumber.trim()) { fail("trnNumber", "TRN Number is required"); return; }

        if (!regCertificateFile) { fail("certificateFile", "PDF certificate is required"); return; }

        try {
            // multipart/form-data (not urlencoded) so the registration
            // certificate file can go up in the same request.
            const formData = new FormData();
            formData.append("first_name", regFirstName.trim());
            formData.append("last_name", regLastName.trim());
            formData.append("user_email", regEmail.trim());
            formData.append("user_contact", `${regCountryCode}${regContact.replace(/\D/g, "")}`);
            formData.append("user_pass", regPassword);
            formData.append("confirm_password", regConfirmPassword);
            formData.append("business_name", regBusinessName.trim());
            formData.append("business_type", regBusinessType);
            formData.append("tax_registration_number", regTrnNumber.trim());
            formData.append("company_address", regFullPickupAddress.trim() || regCompanyAddress.trim() || [regStreet, regCity, regState, regPostalCode].filter(Boolean).join(", "));
            formData.append("pickup_address", regFullPickupAddress.trim());
            formData.append("company_location", regCompanyLocation.trim() || regCity.trim());
            formData.append("pickup_name", regPickupName.trim());
            formData.append("pickup_type", regPickupType);
            formData.append("pickup_street", regStreet.trim());
            formData.append("pickup_city", regCity.trim());
            formData.append("pickup_state", regState.trim());
            formData.append("pickup_postal_code", regPostalCode.trim());
            formData.append("pickup_country", regAddressCountry.trim());
            formData.append("address_country", regAddressCountry.trim());
            formData.append("pickup_latitude", regLatitude);
            formData.append("pickup_longitude", regLongitude);
            formData.append("website", regWebsite.trim());
            formData.append("description", regDescription.trim());
            formData.append("registration_certificate", regCertificateFile);

            const response = await fetch(`${USER_API_URL}/vendorregister`, {
                method: "POST",
                headers: {
                    "Accept": "application/json",
                    "authkey": AUTH_KEY,
                    // No Content-Type header — the browser sets the correct
                    // multipart boundary automatically for FormData.
                },
                body: formData,
            });

            let result: any = {};
            try {
                result = await response.json();
            } catch {
                result = {};
            }

            const payload = result?.data && typeof result.data === "object" ? result.data : result;

            // Treat HTTP failures and all known API error shapes as failures.
            // Some older/deployed vendor-register APIs return duplicate-field
            // errors like { id: "email", message: "Email already exists" }
            // without an `error` boolean, so checking only `result.error` can
            // incorrectly show the success/OTP state.
            const apiError = Boolean(
                !response.ok ||
                result?.error === true ||
                payload?.error === true ||
                result?.id === "email" ||
                result?.id === "mobile" ||
                result?.id === "phone" ||
                result?.id === "contact"
            );

            if (!apiError) {
                setSuccessMsg(payload.message || result.message || "Thanks for registering. Please verify your email with OTP. Your personal details, address and certificate have been submitted for admin review. Please wait for approval before product uploads are enabled.");
                setOtpEmail(regEmail);
                setShowOtpInput(true);
                setRegFirstName("");
                setRegLastName("");
                setRegEmail("");
                setRegContact("");
                setRegCountryCode("+971");
                setRegDescription("");
                setRegPassword("");
                setRegConfirmPassword("");
                setRegBusinessName("");
                setRegBusinessType("shop");
                setRegTrnNumber("");
                setRegCompanyAddress("");
                setRegFullPickupAddress("");
                setRegCompanyLocation("");
                setRegWebsite("");
                setRegCertificateFile(null);
            } else {
                // Keep vendor registration errors inline. The API can explicitly
                // return the field; legacy responses are mapped by message.
                const message = result.message || payload.message ||
                    (response.status === 409 ? "This email or mobile number already exists" : "Registration failed");
                const apiField = result.field || payload.field ||
                    ((result.id === "email" || /^email/i.test(message)) ? "email" :
                    ((result.id === "mobile" || result.id === "phone" || result.id === "contact" || /^mobile|^phone|contact/i.test(message)) ? "contact" : null));

                // Always show server/API validation errors in the top popup.
                setErrorMsg(message);
                if (apiField) {
                    setFieldErrors(prev => ({ ...prev, [apiField]: message }));
                }
            }
        } catch (error) {
            setErrorMsg("Network error: Unable to connect to server");
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMsg("");
        setSuccessMsg("");

        if (!otp.trim()) {
            setErrorMsg("OTP is required");
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${USER_API_URL}/vendorverify-otp`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "Accept": "application/json",
                    "authkey": AUTH_KEY,
                },
                body: new URLSearchParams({
                    user_email: otpEmail,
                    otp: otp,
                }),
            });

            const result = await response.json();

            if (!result.error) {
                setSuccessMsg("Thanks for waiting. Your OTP is verified successfully. Your application, personal details, address and certificate are now under admin review. Please wait until the admin approves your account; product uploads will be enabled after approval.");
                setTimeout(() => {
                    setShowOtpInput(false);
                    setOtp("");
                    setCurrentView("login");
                }, 2000);
            } else {
                setErrorMsg(result.message || "Invalid OTP");
            }
        } catch (error) {
            setErrorMsg("Network error: Unable to connect to server");
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMsg("");
        setSuccessMsg("");

        if (!validateEmail(resetEmail)) {
            setErrorMsg("Please enter a valid email address");
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${USER_API_URL}/vendorforgot-password`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "Accept": "application/json",
                    "authkey": AUTH_KEY,
                },
                body: new URLSearchParams({
                    user_email: resetEmail.trim(),
                }),
            });

            const result = await response.json();

            if (!result.error) {
                setSuccessMsg(result.message || "OTP sent to your email");
                setShowResetOtpInput(true);
            } else {
                setErrorMsg(result.message || "Failed to send OTP");
            }
        } catch (error) {
            setErrorMsg("Network error: Unable to connect to server");
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMsg("");
        setSuccessMsg("");

        if (!resetOtp.trim()) {
            setErrorMsg("OTP is required");
            setLoading(false);
            return;
        }

        if (!showPasswordFields) {
            setShowPasswordFields(true);
            setLoading(false);
            return;
        }

        if (!validateVendorPassword(newPassword)) {
            setErrorMsg("Password must be at least 8 characters and include uppercase, lowercase, number and special character");
            setLoading(false);
            return;
        }

        if (newPassword !== confirmNewPassword) {
            setErrorMsg("Passwords do not match");
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${USER_API_URL}/vendorreset-password`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "Accept": "application/json",
                    "authkey": AUTH_KEY,
                },
                body: new URLSearchParams({
                    user_email: resetEmail.trim(),
                    otp: resetOtp,
                    new_password: newPassword,
                    confirm_password: confirmNewPassword,
                }),
            });

            const result = await response.json();

            if (!result.error) {
                setSuccessMsg(result.message || "Password reset successful!");
                setTimeout(() => {
                    setResetEmail("");
                    setResetOtp("");
                    setNewPassword("");
                    setConfirmNewPassword("");
                    setShowResetOtpInput(false);
                    setShowPasswordFields(false);
                    setCurrentView("login");
                }, 2000);
            } else {
                setErrorMsg(result.message || "Password reset failed");
            }
        } catch (error) {
            setErrorMsg("Network error: Unable to connect to server");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box className="vendor-auth-page">
            <Box className="vendor-auth-shell">
                <Box className="vendor-auth-art">
                    <Box
                        component="img"
                        src={vendorPortalIllustration}
                        alt="Welcome back vendor"
                        className="vendor-auth-art-image"
                    />
                </Box>

                <Card
                    className="vendor-auth-card"
                    sx={{
                        width: "100%",
                        justifySelf: "center",
                        boxShadow: "0 12px 36px rgba(15, 23, 42, 0.10)",
                        borderRadius: 3,
                        backgroundColor: "#ffffff",
                        "& .MuiOutlinedInput-root": { minHeight: 50 },
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#f97316" },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#ea580c" },
                    }}
                >
                <Typography
                    variant="h4"
                    align="center"
                    gutterBottom
                    sx={{
                        fontWeight: "bold",
                        mb: 1,
                        color: "#172033",
                    }}
                >
                    {currentView === "login" && "Vendor Login"}
                    {currentView === "register" && (showOtpInput ? "Verify OTP" : "Sign Up")}
                    {currentView === "forgot" && "Forgot Password"}
                </Typography>

                <Typography
                    variant="body2"
                    align="center"
                    sx={{ mb: 3, color: "#666" }}
                >
                    {currentView === "login" && ""}
                    {currentView === "register" && (showOtpInput ? "Verify your email address" : "Create your vendor account")}
                    {currentView === "forgot" && "Reset your password"}
                </Typography>

                {successMsg && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {successMsg}
                    </Alert>
                )}

                {errorMsg && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {errorMsg}
                    </Alert>
                )}

                {currentView === "login" && (
                    <form onSubmit={handleLogin}>
                        <TextField
                            placeholder="Type your username"
                            type="email"
                            fullWidth
                            value={loginEmail}
                            onChange={(e) => setLoginEmail(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Person sx={{ color: "#999" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            placeholder="Type your password"
                            type={showPassword ? "text" : "password"}
                            fullWidth
                            value={loginPassword}
                            onChange={(e) => setLoginPassword(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                            error={!!fieldErrors.password}
                            helperText={fieldErrors.password || "Minimum 8 characters: uppercase, lowercase, number and special character"}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock sx={{ color: "#999" }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Box sx={{ textAlign: "right", mb: 3 }}>
                            <Link
                                component="button"
                                type="button"
                                variant="body2"
                                onClick={() => setCurrentView("forgot")}
                                sx={{ 
                                    textDecoration: "none",
                                    color: "#667eea",
                                    "&:hover": { textDecoration: "underline" }
                                }}
                            >
                                Forgot password?
                            </Link>
                        </Box>

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading}
                            sx={{
                                py: 1.5,
                                fontSize: "1rem",
                                textTransform: "none",
                                background: "linear-gradient(90deg, #f97316 0%, #ea580c 100%)",
                                borderRadius: 25,
                                mb: 3,
                                "&:hover": {
                                    background: "linear-gradient(90deg, #ea580c 0%, #c2410c 100%)",
                                },
                            }}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1, color: "#fff" }} />
                                    Signing In..
                                </>
                            ) : (
                                "LOGIN"
                            )}
                        </Button>

                        <Box sx={{ textAlign: "center" }}>
                            <Typography variant="body2" sx={{ color: "#666" }}>
                                Don't have an account?{" "}
                                <Link
                                    component="button"
                                    type="button"
                                    onClick={() => setCurrentView("register")}
                                    sx={{ 
                                        textDecoration: "none",
                                        color: "#667eea",
                                        fontWeight: 600,
                                        "&:hover": { textDecoration: "underline" }
                                    }}
                                >
                                    SIGN UP
                                </Link>
                            </Typography>
                        </Box>
                    </form>
                )}

                {currentView === "register" && !showOtpInput && (
                    <form onSubmit={handleRegistration} className="vendor-register-form">
                        <TextField
                            placeholder="First Name"
                            fullWidth
                            value={regFirstName}
                            onChange={(e) => setRegFirstName(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Person sx={{ color: "#999" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            placeholder="Last Name"
                            fullWidth
                            value={regLastName}
                            onChange={(e) => setRegLastName(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Person sx={{ color: "#999" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            placeholder="Email Address"
                            type="email"
                            fullWidth
                            value={regEmail}
                            onChange={(e) => setRegEmail(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Email sx={{ color: "#999" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Box sx={{ display: "flex", gap: 1, mb: 2, alignItems: "flex-start" }}>
                            <TextField select label="Country" value={regCountryCode} onChange={(e) => setRegCountryCode(e.target.value)} disabled={loading} sx={{ width: { xs: "42%", sm: "36%" } }}>
                                {COUNTRY_CODES.map(([code, name, iso]) => (
                                    <MenuItem key={`${iso}-${code}`} value={code}>{name} ({code})</MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="Mobile Number"
                                placeholder="Enter mobile number"
                                fullWidth
                                value={regContact}
                                onChange={(e) => setRegContact(e.target.value.replace(/\D/g, ""))}
                                error={!!fieldErrors.contact}
                                helperText={fieldErrors.contact || `Country code ${regCountryCode} • digits only`}
                                disabled={loading}
                                inputProps={{ inputMode: "tel" }}
                                InputProps={{ startAdornment: <InputAdornment position="start"><Phone sx={{ color: "#f97316" }} /></InputAdornment> }}
                            />
                        </Box>

                        <TextField
                            placeholder="Password"
                            type={showPassword ? "text" : "password"}
                            fullWidth
                            value={regPassword}
                            onChange={(e) => {
                                const value = e.target.value;
                                setRegPassword(value);
                                if (!value) {
                                    setFieldErrors(prev => ({ ...prev, password: "Password is required" }));
                                    setErrorMsg("Password is required");
                                } else if (!validateVendorPassword(value)) {
                                    const message = "Password must be at least 8 characters with uppercase, lowercase, number and special character";
                                    setFieldErrors(prev => ({ ...prev, password: message }));
                                    setErrorMsg(message);
                                } else {
                                    setFieldErrors(prev => { const next = { ...prev }; delete next.password; return next; });
                                    setErrorMsg("");
                                }
                                if (regConfirmPassword && value !== regConfirmPassword) {
                                    const message = "Passwords do not match";
                                    setFieldErrors(prev => ({ ...prev, confirmPassword: message }));
                                } else if (regConfirmPassword) {
                                    setFieldErrors(prev => { const next = { ...prev }; delete next.confirmPassword; return next; });
                                }
                            }}
                            sx={{ mb: 2 }}
                            disabled={loading}
                            error={!!fieldErrors.password}
                            helperText={fieldErrors.password || "8+ characters • uppercase • lowercase • number • special character"}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock sx={{ color: "#999" }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            placeholder="Confirm Password"
                            type={showConfirmPassword ? "text" : "password"}
                            fullWidth
                            value={regConfirmPassword}
                            onChange={(e) => {
                                const value = e.target.value;
                                setRegConfirmPassword(value);
                                if (!value) {
                                    setFieldErrors(prev => ({ ...prev, confirmPassword: "Confirm password is required" }));
                                    setErrorMsg("Confirm password is required");
                                } else if (value !== regPassword) {
                                    const message = "Passwords do not match";
                                    setFieldErrors(prev => ({ ...prev, confirmPassword: message }));
                                    setErrorMsg(message);
                                } else {
                                    setFieldErrors(prev => { const next = { ...prev }; delete next.confirmPassword; return next; });
                                    setErrorMsg("");
                                }
                            }}
                            sx={{ mb: 3 }}
                            disabled={loading}
                            error={!!fieldErrors.confirmPassword}
                            helperText={fieldErrors.confirmPassword}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock sx={{ color: "#999" }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            edge="end"
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Typography variant="subtitle2" sx={{ color: "#555", fontWeight: 700, mt: 1, mb: 1.5 }}>
                            Company details
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#888", display: "block", mb: 2 }}>
                            We use this to verify your business before approving product uploads.
                        </Typography>

                        <TextField
                            placeholder="Company / Business Name"
                            fullWidth
                            value={regBusinessName}
                            onChange={(e) => setRegBusinessName(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                            error={!!fieldErrors.businessName}
                            helperText={fieldErrors.businessName}
                        />

                        <TextField
                            label="Business Description"
                            placeholder="Tell us briefly what your business sells or provides"
                            fullWidth
                            multiline
                            minRows={3}
                            value={regDescription}
                            onChange={(e) => setRegDescription(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                            inputProps={{ maxLength: 500 }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><DescriptionIcon sx={{ color: "#f97316" }} /></InputAdornment> }}
                            helperText={`${regDescription.length}/500`}
                        />

                        <TextField
                            select
                            label="Business Premises Type"
                            fullWidth
                            value={regBusinessType}
                            onChange={(e) => setRegBusinessType(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                        >
                            <MenuItem value="shop">Shop / Retail Store</MenuItem>
                            <MenuItem value="warehouse">Warehouse / Storage</MenuItem>
                            <MenuItem value="both">Shop and Warehouse</MenuItem>
                            <MenuItem value="office">Office / Commercial Premises</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                        </TextField>

                        <TextField
                            label="TRN Number" error={!!fieldErrors.trnNumber}
                            placeholder="Enter your Tax Registration Number"
                            fullWidth
                            value={regTrnNumber}
                            onChange={(e) => setRegTrnNumber(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                            helperText={fieldErrors.trnNumber || "This TRN will be used on your vendor invoice"}
                        />

                        <TextField
                            placeholder="Company Website (optional)"
                            fullWidth
                            value={regWebsite}
                            onChange={(e) => setRegWebsite(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                        />

                        <Accordion
                            expanded={addressExpanded}
                            onChange={(_e, expanded) => setAddressExpanded(expanded)}
                            disableGutters
                            elevation={0}
                            sx={{
                                border: "1px solid #e2e5e9",
                                borderRadius: 2,
                                mb: 2,
                                "&:before": { display: "none" },
                                overflow: "hidden",
                                // Span the full width of the form's two-column
                                // layout so this section renders as one full
                                // width block at the bottom instead of
                                // floating in a single column with empty
                                // space beside it. Covers both CSS grid
                                // (gridColumn) and CSS multi-column
                                // (columnSpan + breakInside) parents.
                                gridColumn: "1 / -1",
                                columnSpan: "all",
                                breakInside: "avoid",
                                width: "100%",
                            }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: "#555", fontWeight: 700 }}>
                                        Pickup Address (optional)
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "#64748b" }}>
                                        Click to view or edit — you can add this later from your profile if you'd rather skip it now.
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                        <TextField label="Pickup Address Name" placeholder="e.g. Main Shop / Warehouse" fullWidth value={regPickupName} onChange={(e) => setRegPickupName(e.target.value)} sx={{ mb: 2 }} disabled={loading} error={!!fieldErrors.pickupName} helperText={fieldErrors.pickupName} />
                        <TextField select label="Pickup Type" fullWidth value={regPickupType} onChange={(e) => setRegPickupType(e.target.value)} sx={{ mb: 2 }} disabled={loading}>
                            <MenuItem value="warehouse">Warehouse</MenuItem><MenuItem value="shop">Shop</MenuItem><MenuItem value="home">Home</MenuItem>
                        </TextField>

                        {/* Street Address gets its own full-width row so long
                            addresses aren't clipped by a narrow column. */}
                        <TextField
                            label="Street Address"
                            placeholder="Street / building / area"
                            fullWidth
                            value={regStreet}
                            onChange={(e) => {
                                const value = e.target.value;
                                setRegStreet(value);
                                const full = [value, regCity, regState, regPostalCode, regAddressCountry].filter(Boolean).join(", ");
                                setRegFullPickupAddress(full);
                                setRegCompanyAddress(full);
                            }}
                            disabled={loading}
                            error={!!fieldErrors.address}
                            sx={{ mb: 1.5 }}
                        />

                        {/* City / State as one evenly-split row (stacks on mobile). */}
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5, mb: 1.5 }}>
                            <TextField
                                label="City"
                                placeholder="City"
                                fullWidth
                                value={regCity}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setRegCity(value);
                                    const full = [regStreet, value, regState, regPostalCode, regAddressCountry].filter(Boolean).join(", ");
                                    setRegFullPickupAddress(full);
                                    setRegCompanyAddress(full);
                                }}
                                disabled={loading}
                            />
                            <TextField
                                label="State"
                                placeholder="State / Province"
                                fullWidth
                                value={regState}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setRegState(value);
                                    const full = [regStreet, regCity, value, regPostalCode, regAddressCountry].filter(Boolean).join(", ");
                                    setRegFullPickupAddress(full);
                                    setRegCompanyAddress(full);
                                }}
                                disabled={loading}
                            />
                        </Box>

                        {/* Country / Postal Code as a second evenly-split row. */}
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5, mb: 1 }}>
                            <TextField
                                label="Country"
                                placeholder="Country"
                                fullWidth
                                value={regAddressCountry}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setRegAddressCountry(value);
                                    const full = [regStreet, regCity, regState, regPostalCode, value].filter(Boolean).join(", ");
                                    setRegFullPickupAddress(full);
                                    setRegCompanyAddress(full);
                                }}
                                disabled={loading}
                            />
                            <TextField
                                label="Postal Code"
                                placeholder="Postal code"
                                fullWidth
                                value={regPostalCode}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setRegPostalCode(value);
                                    const full = [regStreet, regCity, regState, value, regAddressCountry].filter(Boolean).join(", ");
                                    setRegFullPickupAddress(full);
                                    setRegCompanyAddress(full);
                                }}
                                disabled={loading}
                            />
                        </Box>

                        {fieldErrors.address && (
                            <Typography variant="caption" sx={{ color: "#d32f2f", display: "block", mb: 0.5 }}>
                                {fieldErrors.address}
                            </Typography>
                        )}
                        <Typography variant="caption" sx={{ color: "#64748b", display: "block", mb: 0.75 }}>
                            {fetchingLocation ? "Fetching your current location…" : "Address is auto-filled from your current location. You can edit these fields before signing up."}
                        </Typography>
                        <Button
                            type="button"
                            variant="text"
                            onClick={fetchCurrentLocation}
                            disabled={loading || fetchingLocation}
                            sx={{ textTransform: "none", mb: 1.25, px: 0, color: "#ea580c" }}
                        >
                            {fetchingLocation ? "Fetching location…" : "Use my current location"}
                        </Button>
                            </AccordionDetails>
                        </Accordion>

                        <Button
                            variant="outlined"
                            component="label"
                            fullWidth
                            disabled={loading}
                            sx={{ mb: 1, py: 1.2, textTransform: "none", borderRadius: 2, borderColor: "#c9cccf", color: "#444" }}
                        >
                            {regCertificateFile ? regCertificateFile.name : "Upload Business / Shop / Warehouse Certificate"}
                            <input
                                type="file"
                                hidden
                                accept="application/pdf,.pdf"
                                onChange={(e) => {
                                    const file = e.target.files?.[0] || null;
                                    if (file && (file.type !== "application/pdf" || !file.name.toLowerCase().endsWith(".pdf"))) {
                                        setRegCertificateFile(null);
                                        setFieldErrors(prev => ({ ...prev, certificateFile: "Only PDF files are allowed. Please upload a PDF certificate." }));
                                        setErrorMsg("Only PDF files are allowed. Please upload a PDF certificate.");
                                        e.currentTarget.value = "";
                                        return;
                                    }
                                    setFieldErrors(prev => { const next = { ...prev }; delete next.certificateFile; return next; });
                                    setRegCertificateFile(file);
                                }}
                            />
                        </Button>
                        <Typography variant="caption" sx={{ color: "#888", display: "block", mb: 3 }}>
                            PDF only. Examples: Shop & Establishment License, Trade License, Warehouse License, Commercial Registration Certificate.
                        </Typography>                        {fieldErrors.certificateFile && (
                            <Typography variant="caption" color="error" sx={{ display: "block", mb: 2 }}>
                                {fieldErrors.certificateFile}
                            </Typography>
                        )}

                        <Alert severity="info" sx={{ mb: 3 }}>
                            After sign-up you'll verify your email with an OTP. Product uploads unlock once our team reviews and approves your application — we'll email you either way.
                        </Alert>

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading}
                            sx={{
                                py: 1.5,
                                fontSize: "1rem",
                                textTransform: "none",
                                background: "linear-gradient(90deg, #f97316 0%, #ea580c 100%)",
                                borderRadius: 25,
                                mb: 3,
                                "&:hover": {
                                    background: "linear-gradient(90deg, #ea580c 0%, #c2410c 100%)",
                                },
                            }}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1, color: "#fff" }} />
                                    Registering...
                                </>
                            ) : (
                                "SUBMIT APPLICATION"
                            )}
                        </Button>

                        <Box sx={{ textAlign: "center" }}>
                            <Typography variant="body2" sx={{ color: "#666" }}>
                                Already have an account?{" "}
                                <Link
                                    component="button"
                                    type="button"
                                    onClick={() => setCurrentView("login")}
                                    sx={{ 
                                        textDecoration: "none",
                                        color: "#667eea",
                                        fontWeight: 600,
                                        "&:hover": { textDecoration: "underline" }
                                    }}
                                >
                                    LOGIN
                                </Link>
                            </Typography>
                        </Box>
                    </form>
                )}

                {currentView === "register" && showOtpInput && (
                    <form onSubmit={handleVerifyOtp}>
                        <Typography variant="body2" sx={{ mb: 2, color: "#666", textAlign: "center" }}>
                            An OTP has been sent to {otpEmail}
                        </Typography>

                        <TextField
                            placeholder="Enter OTP"
                            fullWidth
                            value={otp}
                            onChange={(e) => setOtp(e.target.value)}
                            sx={{ mb: 3 }}
                            disabled={loading}
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading}
                            sx={{
                                py: 1.5,
                                fontSize: "1rem",
                                textTransform: "none",
                                background: "linear-gradient(90deg, #f97316 0%, #ea580c 100%)",
                                borderRadius: 25,
                                mb: 3,
                                "&:hover": {
                                    background: "linear-gradient(90deg, #ea580c 0%, #c2410c 100%)",
                                },
                            }}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1, color: "#fff" }} />
                                    Verifying...
                                </>
                            ) : (
                                "Verify OTP"
                            )}
                        </Button>

                        <Box sx={{ textAlign: "center" }}>
                            <Link
                                component="button"
                                type="button"
                                onClick={() => {
                                    setShowOtpInput(false);
                                    setCurrentView("login");
                                }}
                                sx={{ 
                                    textDecoration: "none",
                                    color: "#667eea",
                                    "&:hover": { textDecoration: "underline" }
                                }}
                            >
                                Back to Login
                            </Link>
                        </Box>
                    </form>
                )}

                {currentView === "forgot" && !showResetOtpInput && (
                    <form onSubmit={handleForgotPassword}>
                        <TextField
                            placeholder="Enter your email address"
                            type="email"
                            fullWidth
                            value={resetEmail}
                            onChange={(e) => setResetEmail(e.target.value)}
                            sx={{ mb: 3 }}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Email sx={{ color: "#999" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading}
                            sx={{
                                py: 1.5,
                                fontSize: "1rem",
                                textTransform: "none",
                                background: "linear-gradient(90deg, #f97316 0%, #ea580c 100%)",
                                borderRadius: 25,
                                mb: 3,
                                "&:hover": {
                                    background: "linear-gradient(90deg, #ea580c 0%, #c2410c 100%)",
                                },
                            }}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1, color: "#fff" }} />
                                    Sending OTP...
                                </>
                            ) : (
                                "Send OTP"
                            )}
                        </Button>

                        <Box sx={{ textAlign: "center" }}>
                            <Link
                                component="button"
                                type="button"
                                onClick={() => setCurrentView("login")}
                                sx={{ 
                                    textDecoration: "none",
                                    color: "#667eea",
                                    "&:hover": { textDecoration: "underline" }
                                }}
                            >
                                Back to Login
                            </Link>
                        </Box>
                    </form>
                )}

                {currentView === "forgot" && showResetOtpInput && (
                    <form onSubmit={handleResetPassword}>
                        <Typography variant="body2" sx={{ mb: 2, color: "#666", textAlign: "center" }}>
                            Enter the OTP sent to {resetEmail}
                        </Typography>

                        <TextField
                            placeholder="Enter OTP"
                            fullWidth
                            value={resetOtp}
                            onChange={(e) => setResetOtp(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                        />

                        {showPasswordFields && (
                            <>
                                <TextField
                                    placeholder="New Password"
                                    type={showPassword ? "text" : "password"}
                                    fullWidth
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    sx={{ mb: 2 }}
                                    disabled={loading}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock sx={{ color: "#999" }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                                    placeholder="Confirm New Password"
                                    type={showConfirmPassword ? "text" : "password"}
                                    fullWidth
                                    value={confirmNewPassword}
                                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                                    sx={{ mb: 3 }}
                                    disabled={loading}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock sx={{ color: "#999" }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    edge="end"
                                                >
                                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </>
                        )}

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading}
                            sx={{
                                py: 1.5,
                                fontSize: "1rem",
                                textTransform: "none",
                                background: "linear-gradient(90deg, #f97316 0%, #ea580c 100%)",
                                borderRadius: 25,
                                mb: 3,
                                "&:hover": {
                                    background: "linear-gradient(90deg, #ea580c 0%, #c2410c 100%)",
                                },
                            }}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1, color: "#fff" }} />
                                    {showPasswordFields ? "Resetting..." : "Verifying..."}
                                </>
                            ) : (
                                showPasswordFields ? "Reset Password" : "Verify OTP"
                            )}
                        </Button>

                        <Box sx={{ textAlign: "center" }}>
                            <Link
                                component="button"
                                type="button"
                                onClick={() => {
                                    setShowResetOtpInput(false);
                                    setShowPasswordFields(false);
                                    setCurrentView("login");
                                }}
                                sx={{ 
                                    textDecoration: "none",
                                    color: "#667eea",
                                    "&:hover": { textDecoration: "underline" }
                                }}
                            >
                                Back to Login
                            </Link>
                        </Box>
                    </form>
                )}
            </Card>
            </Box>

            <Snackbar
                open={!!errorMsg}
                autoHideDuration={5000}
                onClose={() => setErrorMsg("")}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                sx={{ zIndex: 99999 }}
            >
                <Alert severity="error" variant="filled" onClose={() => setErrorMsg("")} sx={{ minWidth: { xs: "90vw", sm: 420 }, maxWidth: "calc(100vw - 24px)", boxShadow: 6 }}>
                    {errorMsg}
                </Alert>
            </Snackbar>

            {/* Vendor account restriction dialog. Sized to roughly half to
                three-fifths of the viewport width on desktop per request —
                the earlier compact ~320px version read as too small once the
                support contact line was added. Still capped and responsive
                on mobile. */}
            <Dialog
                open={accountStatusDialog.open}
                onClose={() => setAccountStatusDialog(v => ({...v, open:false}))}
                PaperProps={{ sx: { width: { xs: "92vw", sm: "58vw", md: "50vw" }, maxWidth: 640, borderRadius: 2, m: 1 } }}
            >
                <DialogContent sx={{ px: 3, py: 2.5 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#c62828", mb: 1 }}>
                        {accountStatusDialog.status === "blocked" ? "Account Blocked" : "Application Rejected"}
                    </Typography>
                    <Typography sx={{ fontSize: 14, lineHeight: 1.5 }}>
                        {accountStatusDialog.reason}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 1.5 }}>
                        Contact the administrator for assistance.
                    </Typography>
                    {(supportContact.email || supportContact.contact) && (
                        <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1.5, bgcolor: "#f5f5f5", display: "flex", flexDirection: "column", gap: 0.5 }}>
                            {supportContact.email && (
                                <Typography sx={{ fontSize: 13, display: "flex", alignItems: "center", gap: 1 }}>
                                    <EmailOutlinedIcon sx={{ fontSize: 17, color: "text.secondary" }} />
                                    <Link href={`mailto:${supportContact.email}`} underline="hover">{supportContact.email}</Link>
                                </Typography>
                            )}
                            {supportContact.contact && (
                                <Typography sx={{ fontSize: 13, display: "flex", alignItems: "center", gap: 1 }}>
                                    <PhoneOutlinedIcon sx={{ fontSize: 17, color: "text.secondary" }} />
                                    <Link href={`tel:${supportContact.contact}`} underline="hover">{supportContact.contact}</Link>
                                </Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
                    <Button variant="contained" onClick={() => { setAccountStatusDialog(v => ({...v, open:false})); setCurrentView("login"); }}>OK</Button>
                </DialogActions>
            </Dialog>

            {/* Post-login pickup address confirmation */}
            <Dialog open={addressPromptOpen} maxWidth="xs" fullWidth>
                <DialogTitle>Confirm Pickup Address</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        We have this pickup address on file for your account:
                    </Typography>
                    {existingAddress && (
                        <Box sx={{ p: 2, borderRadius: 2, bgcolor: "#f5f5f5", mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {existingAddress.name}
                            </Typography>
                            {existingAddress.address && (
                                <Typography variant="body2" color="text.secondary">
                                    {existingAddress.address}
                                </Typography>
                            )}
                        </Box>
                    )}
                    <Typography variant="body2" color="text.secondary">
                        Use this address, or add a new one for this login.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleAddNewAddress} sx={{ textTransform: "none" }}>
                        Add New Address
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleUseExistingAddress}
                        sx={{ textTransform: "none", background: "linear-gradient(90deg, #f97316 0%, #ea580c 100%)" }}
                    >
                        Use This Address
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default VendorLogin;